import {useEffect, useState, useMemo, useDeferredValue} from 'react'
import ProjectListItems from './ProjectListItems'
import {StyledTypography, StyledBox} from '../AdminPanelStyling'
import BreadcrumbsComponent from '../common/BreadcrumbsRoute/BreadcrumbComponent'
import {MainBoxClientLanding} from '../CreateClient/CreateClientStyles'
import {
  StyledBackButton,
  StyledBackIcon,
  StyledDiv,
  StyledTabs,
  StyledTab,
  StyledSearchField,
  StyledSearchIcon
} from './ProjectListLandingStyles'
import LoadingIcon from '../common/LoadingIcon/LoadingIcon'
import {useNavigate, useParams} from 'react-router-dom'
import {useGetGroupDetails} from '../api/getProjectDetails'
import DataRefetchOnError from '../common/DataFetchError/DataRefetchOnError'
import {CenteredErrorContainer} from '../ProjectDetails/ProjectDetailStyle'
import UserMessages from '../common/Errors/UserMessages.json'
import ErrorComponent from '../../components/Error/ErrorComponent'
import getStatusCode from '../common/Helpers/getStatusCode'
import EmptyListAlertInfo from '../common/EmptyListAlertInfo/EmptyListAlertInfoIndex'
import loadingTitles from '../../appConfig/loadingTitles.config.json'
import PropTypes from 'prop-types'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import {EditRounded, AddCircleRounded} from '@mui/icons-material'
import InputAdornment from '@mui/material/InputAdornment'
import Button from '@mui/material/Button'
import ProjectAdminList from './ProjectAdminListComponent'
import ClientNameEditModal from '../common/ClientNameEditModal/ClientNameEditModal'
import AddProjectModel from '../ManageClientPermissions/AddProjectModel'
import {useRolePermissions} from '../../hooks/useRolesPermissions'
import SnackbarComponent from '../common/Snackbar/SnackbarComponent'
import SnackbarMessages from '../common/Snackbar/SnackbarMessages.json'

const ProjectListLanding = ({isAdminPanelOpen, setIsAdminPanelOpen}) => {
  const {clientId} = useParams()

  const {group, loadingGroups, errorGroups, refetchGroups} = useGetGroupDetails(clientId)

  const navigate = useNavigate()

  const {isSuperAdmin, isCustomerSupport} = useRolePermissions()

  const [activeTab, setActiveTab] = useState(0)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [showAddProjectModal, setShowAddProjectModal] = useState(false)
  const [successSnackbar, setSuccessSnackbar] = useState({show: false, message: ''})
  const deferredSearchQuery = useDeferredValue(searchQuery)
  const groupList = group?.groupList
  const hasGroupList = !!groupList

  useEffect(() => {
    setIsAdminPanelOpen(true)
  })

  const [navigationProps, setNavigationProps] = useState([])

  useEffect(() => {
    if (!loadingGroups && hasGroupList) {
      setNavigationProps([
        {
          id: groupList.CLIENT_ID,
          key: 'group',
          name: groupList.CLIENT_NAME
        }
      ])
    }
  }, [groupList, hasGroupList, loadingGroups])

  const projects = useMemo(() => groupList?.GROUPList ?? [], [groupList?.GROUPList])

  const filteredProjects = useMemo(() => {
    const normalizedSearchQuery = deferredSearchQuery.trim().toLowerCase()
    if (!normalizedSearchQuery) return projects
    return projects.filter((project) =>
      String(project?.GROUP_NAME ?? '')
        .toLowerCase()
        .includes(normalizedSearchQuery)
    )
  }, [projects, deferredSearchQuery])

  const existingProjectNames = useMemo(() => projects.map((project) => project?.GROUP_NAME || ''), [projects])

  const refetchChart = (event) => {
    event.preventDefault()
    refetchGroups()
  }

  const handleOpenEditModal = () => {
    setIsEditModalOpen(true)
  }

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false)
  }

  const handleClientUpdated = async () => {
    await refetchGroups()
    setSuccessSnackbar({show: true, message: SnackbarMessages.clientUpdateSuccess})
  }

  const handleToggleProjectModel = (event) => {
    if (event?.preventDefault) {
      event.preventDefault()
    }
    setShowAddProjectModal(!showAddProjectModal)
  }

  const getErrorMessage = () => {
    const {message} = errorGroups
    const errorClient = `client id ${clientId} `
    if (getStatusCode(message) === 404) {
      return `Sorry, we couldn't find that page with ${errorClient}`
    } else if (getStatusCode(message) === 401) {
      return `Sorry, you are not authorized to access ${errorClient}`
    }
  }

  const errorComponent = (code) => {
    return <ErrorComponent isAdminPanelOpen={isAdminPanelOpen} errorCode={code} errorMsg={getErrorMessage()} />
  }

  if (!loadingGroups && errorGroups) {
    const {message} = errorGroups
    const code = getStatusCode(message)
    return code === 404 || code === 401 ? (
      errorComponent(code)
    ) : (
      <MainBoxClientLanding component='main'>
        <StyledBox>
          <CenteredErrorContainer>
            <DataRefetchOnError onTryAgain={(event) => refetchChart(event)} />
          </CenteredErrorContainer>
        </StyledBox>
      </MainBoxClientLanding>
    )
  }
  return (
    <div>
      {showAddProjectModal && (
        <AddProjectModel
          open={showAddProjectModal}
          onClose={handleToggleProjectModel}
          onSave={refetchGroups}
          clientId={clientId}
          existingProjects={existingProjectNames}
        />
      )}
      <MainBoxClientLanding component='main'>
        <StyledBox>
          {loadingGroups && <LoadingIcon title={loadingTitles.projectList} />}
          {!loadingGroups && hasGroupList && navigationProps && (
            <Box display='flex' flexDirection='column' gap={1} margin={0}>
              <BreadcrumbsComponent props={navigationProps} isProjectView={false} hideBackButton />
              <Box display='flex' alignItems='center'>
                <StyledBackButton onClick={() => navigate('/adminpanel/')} aria-label='Go back'>
                  <StyledBackIcon />
                </StyledBackButton>
                <StyledTypography variant='h5' tabIndex={0} title={groupList.CLIENT_NAME}>
                  {groupList.CLIENT_NAME}
                </StyledTypography>
              </Box>
              <br />
              <Box display='flex' alignItems='center' gap={1}>
                <StyledTypography variant='h5' tabIndex={0}>
                  Client Details
                </StyledTypography>
                {}
                {(isSuperAdmin() || isCustomerSupport()) && (
                  <IconButton color='primary' size='small' onClick={handleOpenEditModal} aria-label='Edit client name'>
                    <EditRounded />
                  </IconButton>
                )}
              </Box>
              <Box display='flex' alignItems='center' gap={3}>
                <Typography variant='subtitle1' tabIndex={0} fontWeight={600}>
                  Client Name:
                </Typography>
                <Typography variant='subtitle1' tabIndex={0}>
                  {groupList.CLIENT_NAME}
                </Typography>
              </Box>

              <StyledTabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
                <StyledTab label='Client Projects' />
                {(isSuperAdmin() || isCustomerSupport()) && <StyledTab label='Admins of this Client' />}
              </StyledTabs>
            </Box>
          )}
          {!loadingGroups && hasGroupList && activeTab === 0 && (
            <>
              <Box display='flex' gap={2} alignItems='center' mt={3}>
                <StyledSearchField
                  size='medium'
                  placeholder='Search Projects'
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start'>
                        <StyledSearchIcon />
                      </InputAdornment>
                    )
                  }}
                />
                <Button
                  variant='outlined'
                  size='medium'
                  startIcon={<AddCircleRounded />}
                  onClick={handleToggleProjectModel}
                >
                  Add New Project
                </Button>
              </Box>
              {filteredProjects.length === 0 ||
              (!loadingGroups && hasGroupList && activeTab === 0 && projects.length === 0) ? (
                <StyledDiv>
                  <EmptyListAlertInfo message={UserMessages.projectNotFound} />
                </StyledDiv>
              ) : (
                <ProjectListItems
                  defaultRowHeight={77}
                  data={filteredProjects}
                  clientId={groupList.CLIENT_ID}
                  loadingGroups={loadingGroups}
                  error={false}
                  clientName={groupList.CLIENT_NAME}
                />
              )}
            </>
          )}
          {!loadingGroups && hasGroupList && activeTab === 1 && (
            <Box mt={3}>
              <ProjectAdminList />
            </Box>
          )}
        </StyledBox>
      </MainBoxClientLanding>
      <ClientNameEditModal
        open={isEditModalOpen}
        onClose={handleCloseEditModal}
        clientId={clientId}
        clientName={group?.groupList?.CLIENT_NAME}
        onSave={handleClientUpdated}
      />
      <SnackbarComponent
        showNotification={successSnackbar.show}
        setShowNotification={(show) => setSuccessSnackbar((prev) => ({...prev, show}))}
        message={successSnackbar.message}
        error={false}
      />
    </div>
  )
}

ProjectListLanding.propTypes = {
  isAdminPanelOpen: PropTypes.bool.isRequired,
  setIsAdminPanelOpen: PropTypes.func.isRequired
}

export default ProjectListLanding
